import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import {
  $Button,
  $Popconfirm,
  $Popover,
  $Drawer,
  $Breadcrumb,
  $Affix,
  $PageHeader,
} from "us.common/components";
import {
  MoreOutlined,
  PushpinOutlined,
  HourglassOutlined,
  DeleteOutlined,
  PushpinFilled,
  SaveOutlined,
} from "us.icons";
import { followUps } from "us.collection.followup/actions";
import { RootState } from "us.helper/types";
import { IItem } from "./Interfaces";
import { $Tooltip } from "us.common/components";
import {
  FollowUpHistory,
  FollowUpActionHistory,
} from "us.collection.followup/components/FollowUps/Components";
import { PinType } from "us.collection.followup/components/FollowUps/Components/FollowUpList/Constants";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { checkFilterPermission } from "us.collection.followup/functions";
import { PermissionType } from "us.collection.followup/constants";

const { item, historyDrawer, deleteFilter } = followUps;

const FollowUpItem: React.FC<PropsFromRedux & IItem> = (props) => {
  const { t } = useTranslation();
  const {
    followUpItem,
    type,
    pinStatus,
    isSelected,
    historyDrawer,
    AuthorizationProfile,
    onSelect,
    deleteFilter,
    unPinFollowUpItem,
    manageFollowUpHistoryDrawer,
  } = props;
  const location = useLocation();
  const state = location.state as any;
  const [followupHistoryVisible, setFollowupHistoryVisible] =
    useState<boolean>(false);
  const [popVisible, setPopVisible] = useState<number>(-1);

  const {
    followupId,
    followupDisplayName,
    caseCount,
    filterId,
    isFilter,
    followupName,
    createdUser,
  } = followUpItem;

  const currentUser: any = new Azure.ADAuth().currentUser();
  
  const followupHistoryClose = () => {
    manageFollowUpHistoryDrawer &&
      manageFollowUpHistoryDrawer({
        isActionHistoryVisible: false,
      });
    setFollowupHistoryVisible(false);
  };

  const currentUserEmail = useMemo(() => {
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile
    );
  }, [AuthorizationProfile]);

  const isDeletable = useMemo(() => {
    return checkFilterPermission(
      PermissionType.DELETE,
      AuthorizationProfile?.authorizationProfile.featureList
    );
  }, [AuthorizationProfile?.authorizationProfile]);

  return (
    <>
      <Link
        to={`/follow-up/${followupId}${
          filterId && Number(filterId) > 0 ? `/${filterId}` : ""
        }`}
        state={state}
        onClick={onSelect}
      >
        <div
          className={`fl-cat-item 
						${isSelected ? "fl-cat-item-selected" : ""}
						${
              followupName?.trim()?.toLowerCase() === "common follow-up"
                ? "fl-cat-item-common"
                : ""
            }
						`}
        >
          <div className="fl-cat-content" key={`${followupId}`}>
            <div className="fl-cat-title">
              <$Tooltip
                placement="topLeft"
                title={
                  isFilter
                    ? t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SAVED_FOLLOWUP")
                    : type === PinType.PINNED
                    ? t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PINNED_FOLLOWUP")
                    : t(
                        "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.UNPINNED_FOLLOWUP"
                      )
                }
              >
                {isFilter ? (
                  <SaveOutlined className="mr-1" />
                ) : type === PinType.PINNED ? (
                  <PushpinFilled className="mr-1" />
                ) : (
                  <PushpinOutlined className="mr-1" />
                )}
              </$Tooltip>
              <$Tooltip placement={"topLeft"} title={followupDisplayName}>
                {followupDisplayName}
              </$Tooltip>
            </div>
            {!isFilter && (
              <div className="fl-cat-count">
                <span>
                  <$Tooltip placement={"topLeft"} title={caseCount}>
                    {caseCount}
                  </$Tooltip>
                </span>
              </div>
            )}
          </div>
          <div className="fl-cat-action">
            <$Popover
              placement="left"
              trigger="click"
              open={followupId == popVisible}
              onOpenChange={(visible: boolean) => {
                setPopVisible(visible ? followupId : -1);
              }}
              content={
                <>
                  {isFilter && (
                    <div className="table-more-content">
                      <$Popconfirm
                        title={t(
                          "US.COLLECTION.COMMON:COMMON.SURE_TO_DELETE_?"
                        )}
                        data-testid="popUpConfirm-box"
                        disabled={!isDeletable}
                        onConfirm={() => {
                          deleteFilter &&
                            deleteFilter({ filterId, currentUserEmail });
                          setPopVisible(-1);
                        }}
                      >
                        <div
                          className={`d-flex flex-row more-item ${
                            isDeletable ? "text-error" : "text-muted"
                          }`}
                          data-testid="delete-btn"
                        >
                          <div className="p-1">
                            <DeleteOutlined />
                          </div>
                          <div className="p-1">
                            {t("US.COLLECTION.COMMON:COMMON.DELETE")}
                          </div>
                        </div>
                      </$Popconfirm>
                    </div>
                  )}
                  {!isFilter && (
                    <div className="table-more-content">
                      <$Popconfirm
                        title={
                          type == PinType.PINNED
                            ? t(
                                "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.UNPIN_CONFIRM"
                              )
                            : t(
                                "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PIN_CONFIRM"
                              )
                        }
                        onConfirm={() => {
                          unPinFollowUpItem &&
                            unPinFollowUpItem({
                              caselistQueryId: followupId,
                              pinStatus: type == PinType.PINNED ? false : true,
                              userEmail: currentUserEmail,
                            });
                          setPopVisible(-1);
                        }}
                        onCancel={() => setPopVisible(-1)}
                      >
                        <div className="d-flex mb-2 flex-row more-item">
                          <div className="p-1">
                            <PushpinOutlined />
                          </div>
                          <div className="p-1">
                            {type == PinType.PINNED
                              ? t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.UNPIN")
                              : t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PIN")}
                          </div>
                        </div>
                      </$Popconfirm>
                      <div
                        className="d-flex flex-row more-item"
                        onClick={() => {
                          setFollowupHistoryVisible(true);
                          setPopVisible(-1);
                        }}
                      >
                        <div className="p-1">
                          <HourglassOutlined />
                        </div>
                        <div className="p-1">
                          {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.HISTORY")}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              }
              destroyTooltipOnHide
            >
              {!(isFilter && createdUser !== currentUserEmail) && (
                <$Button
                  type="link"
                  icon={<MoreOutlined />}
                  size="small"
                  loading={!isFilter && pinStatus.data?.includes(followupId)}
                />
              )}
            </$Popover>
          </div>
        </div>
      </Link>
      <$Drawer
        title={t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.HISTORY")}
        width={"90%"}
        open={followupHistoryVisible}
        onClose={followupHistoryClose}
        destroyOnClose
      >
        {!historyDrawer.isActionHistoryVisible && (
          <FollowUpHistory
            followUpItem={followUpItem}
            onClose={followupHistoryClose}
          />
        )}
        {historyDrawer.isActionHistoryVisible && (
          <>
            <$Breadcrumb>
              <$Breadcrumb.Item
                onClick={() =>
                  manageFollowUpHistoryDrawer &&
                  manageFollowUpHistoryDrawer({
                    isActionHistoryVisible: false,
                  })
                }
              >
                <a href="#">
                  {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.HISTORY")}
                </a>
              </$Breadcrumb.Item>
              <$Breadcrumb.Item>
                {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ACTION_HISTORY")}
              </$Breadcrumb.Item>
            </$Breadcrumb>
            <$Affix offsetTop={80}>
              <div className="page-header">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0 py-0 mr-n3"
                    onBack={() =>
                      manageFollowUpHistoryDrawer &&
                      manageFollowUpHistoryDrawer({
                        isActionHistoryVisible: false,
                      })
                    }
                    title={t(
                      "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ACTION_HISTORY"
                    )}
                  />
                </div>
              </div>
            </$Affix>
            <div className="mt-4">
              <FollowUpActionHistory onClose={followupHistoryClose} />
            </div>
          </>
        )}
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { followUps, AuthorizationProfile } = state;
  const { historyDrawer, pinStatus } = followUps;
  return { historyDrawer, pinStatus, AuthorizationProfile };
};

const mapDispatchToProps = {
  unPinFollowUpItem: item.unPin,
  manageFollowUpHistoryDrawer: historyDrawer.manage,
  deleteFilter: deleteFilter.delete,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FollowUpItem);
